import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, normalizeProps as _normalizeProps, guardReactiveProps as _guardReactiveProps, unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "admin-notice-write" }
const _hoisted_2 = { class: "page-header" }
const _hoisted_3 = { class: "header-content" }
const _hoisted_4 = { class: "page-subtitle" }
const _hoisted_5 = { class: "editor-container" }
const _hoisted_6 = {
  key: 0,
  class: "existing-files mt-4"
}
const _hoisted_7 = { class: "file-name" }
const _hoisted_8 = { class: "file-size" }

import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import axios from 'axios';
import { QuillEditor } from '@vueup/vue-quill';
import '@vueup/vue-quill/dist/vue-quill.snow.css';

interface NoticeFile {
  nf_idx: number;
  nf_original_name: string;
  nf_size: number;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'AdminNoticeWrite',
  setup(__props) {

const route = useRoute();
const router = useRouter();
const store = useStore();

const formRef = ref();
const quillRef = ref();
const formValid = ref(false);
const saving = ref(false);
const snackbar = ref({ show: false, text: '', color: 'info' });

// 폼 데이터
const form = ref({
  nc_category: 'NOTICE' as 'NOTICE' | 'UPDATE' | 'EVENT' | 'URGENT',
  nc_title: '',
  nc_content: ''
});

const newFiles = ref<File[]>([]);
const existingFiles = ref<NoticeFile[]>([]);
const filesToDelete = ref<number[]>([]);

// 수정 모드 여부
const isEdit = computed(() => !!route.params.ncIdx);
const isAdmin = computed(() => store.state.user?.m_role === 'admin');

// 카테고리 옵션
const categoryOptions = computed(() => {
  const options = [
    { text: '공지사항', value: 'NOTICE', icon: 'mdi-bullhorn', color: 'primary' },
    { text: '이벤트', value: 'EVENT', icon: 'mdi-party-popper', color: 'success' },
    { text: '긴급', value: 'URGENT', icon: 'mdi-alert', color: 'error' }
  ];
  
  // 업데이트는 관리자만 작성 가능
  if (isAdmin.value) {
    options.splice(1, 0, { text: '업데이트', value: 'UPDATE', icon: 'mdi-update', color: 'info' });
  }
  
  return options;
});

// Quill 에디터 옵션
const editorOptions = {
  theme: 'snow',
  placeholder: '공지사항 내용을 입력하세요...',
  modules: {
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
        [{ 'size': ['small', false, 'large', 'huge'] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        [{ 'align': [] }],
        ['link', 'image'],
        ['clean']
      ],
      handlers: {
        image: imageHandler
      }
    }
  }
};

// 이미지 핸들러
function imageHandler() {
  const input = document.createElement('input');
  input.setAttribute('type', 'file');
  input.setAttribute('accept', 'image/*');
  input.click();
  
  input.onchange = async () => {
    const file = input.files?.[0];
    if (!file) return;
    
    // 파일 크기 체크 (5MB)
    if (file.size > 5 * 1024 * 1024) {
      showSnackbar('이미지 크기는 5MB 이하여야 합니다.', 'error');
      return;
    }
    
    const formData = new FormData();
    formData.append('image', file);
    
    try {
      const response = await axios.post('/api/notice/upload-image', formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success) {
        const quill = quillRef.value?.getQuill();
        if (quill) {
          const range = quill.getSelection(true);
          quill.insertEmbed(range.index, 'image', response.data.url);
          quill.setSelection(range.index + 1);
        }
      }
    } catch (error) {
      console.error('이미지 업로드 오류:', error);
      showSnackbar('이미지 업로드에 실패했습니다.', 'error');
    }
  };
}

// 에디터 준비 완료
const onEditorReady = () => {
  // 필요한 초기화
};

// 기존 공지 로드 (수정 시)
const loadNotice = async () => {
  const ncIdx = route.params.ncIdx;
  if (!ncIdx) return;
  
  try {
    const response = await axios.get(`/api/notice/detail/${ncIdx}`, {
      params: { increaseHit: 'false' },
      withCredentials: true
    });
    
    if (response.data.success) {
      const notice = response.data.notice;
      form.value.nc_category = notice.nc_category || 'NOTICE';
      form.value.nc_title = notice.nc_title;
      form.value.nc_content = notice.nc_content;
      existingFiles.value = response.data.files || [];
    }
  } catch (error) {
    console.error('공지사항 로드 오류:', error);
    showSnackbar('공지사항을 불러오는데 실패했습니다.', 'error');
  }
};

// 저장
const saveNotice = async () => {
  if (!formRef.value?.validate()) return;
  
  saving.value = true;
  
  try {
    let ncIdx: number;
    
    // nc_type은 작성자 역할에 따라 자동 결정
    const nc_type = isAdmin.value ? 'ADMIN' : 'CENTER';
    
    if (isEdit.value) {
      // 수정
      const response = await axios.put(`/api/notice/update/${route.params.ncIdx}`, {
        nc_type: nc_type,
        nc_category: form.value.nc_category,
        nc_title: form.value.nc_title,
        nc_content: form.value.nc_content
      }, { withCredentials: true });
      
      if (!response.data.success) {
        throw new Error(response.data.message);
      }
      
      ncIdx = parseInt(route.params.ncIdx as string);
      
      // 삭제할 파일 처리
      for (const nfIdx of filesToDelete.value) {
        await axios.delete(`/api/notice/file/${nfIdx}`, { withCredentials: true });
      }
    } else {
      // 등록
      const response = await axios.post('/api/notice/create', {
        nc_type: nc_type,
        nc_category: form.value.nc_category,
        nc_title: form.value.nc_title,
        nc_content: form.value.nc_content
      }, { withCredentials: true });
      
      if (!response.data.success) {
        throw new Error(response.data.message);
      }
      
      ncIdx = response.data.insertId;
    }
    
    // 새 첨부파일 업로드
    if (newFiles.value && newFiles.value.length > 0) {
      const formData = new FormData();
      newFiles.value.forEach(file => {
        formData.append('files', file);
      });
      
      await axios.post(`/api/notice/upload/${ncIdx}`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    }
    
    showSnackbar(isEdit.value ? '공지사항이 수정되었습니다.' : '공지사항이 등록되었습니다.', 'success');
    
    setTimeout(() => {
      router.push(`/admin/notice/${ncIdx}`);
    }, 1000);
    
  } catch (error: any) {
    console.error('저장 오류:', error);
    showSnackbar(error.message || '저장에 실패했습니다.', 'error');
  } finally {
    saving.value = false;
  }
};

// 기존 파일 삭제 표시
const removeExistingFile = (file: NoticeFile) => {
  existingFiles.value = existingFiles.value.filter(f => f.nf_idx !== file.nf_idx);
  filesToDelete.value.push(file.nf_idx);
};

// 파일 크기 포맷
const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

// 네비게이션
const goBack = () => {
  router.push('/admin/notice');
};

const showSnackbar = (text: string, color: string = 'info') => {
  snackbar.value = { show: true, text, color };
};


onMounted(() => {
  if (isEdit.value) {
    loadNotice();
  }
});

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_list_item = _resolveComponent("v-list-item")!
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_v_select = _resolveComponent("v-select")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_file_input = _resolveComponent("v-file-input")!
  const _component_v_form = _resolveComponent("v-form")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_divider = _resolveComponent("v-divider")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_snackbar = _resolveComponent("v-snackbar")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createVNode(_component_v_btn, {
          icon: "",
          variant: "text",
          color: "white",
          onClick: goBack,
          class: "mr-2"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_v_icon, null, {
              default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                _createTextVNode("mdi-arrow-left", -1)
              ]))]),
              _: 1
            })
          ]),
          _: 1
        }),
        _createElementVNode("div", null, [
          _cache[7] || (_cache[7] = _createElementVNode("h1", { class: "page-title" }, "공지사항", -1)),
          _createElementVNode("p", _hoisted_4, _toDisplayString(isEdit.value ? '수정하기' : '새 공지 작성'), 1)
        ])
      ])
    ]),
    _createVNode(_component_v_card, {
      class: "write-card",
      elevation: "1"
    }, {
      default: _withCtx(() => [
        _createVNode(_component_v_card_text, { class: "pa-6" }, {
          default: _withCtx(() => [
            _createVNode(_component_v_form, {
              ref_key: "formRef",
              ref: formRef,
              modelValue: formValid.value,
              "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((formValid).value = $event))
            }, {
              default: _withCtx(() => [
                _createVNode(_component_v_row, null, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_col, {
                      cols: "12",
                      md: "4"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_select, {
                          modelValue: form.value.nc_category,
                          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((form.value.nc_category) = $event)),
                          items: categoryOptions.value,
                          "item-title": "text",
                          "item-value": "value",
                          label: "카테고리",
                          variant: "outlined",
                          rules: [v => !!v || '카테고리를 선택해주세요']
                        }, {
                          item: _withCtx(({ item, props }) => [
                            _createVNode(_component_v_list_item, _normalizeProps(_guardReactiveProps(props)), {
                              prepend: _withCtx(() => [
                                _createVNode(_component_v_icon, {
                                  color: item.raw.color
                                }, {
                                  default: _withCtx(() => [
                                    _createTextVNode(_toDisplayString(item.raw.icon), 1)
                                  ]),
                                  _: 2
                                }, 1032, ["color"])
                              ]),
                              _: 2
                            }, 1040)
                          ]),
                          selection: _withCtx(({ item }) => [
                            _createVNode(_component_v_chip, {
                              color: item.raw.color,
                              size: "small",
                              variant: "flat"
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_component_v_icon, {
                                  start: "",
                                  size: "small"
                                }, {
                                  default: _withCtx(() => [
                                    _createTextVNode(_toDisplayString(item.raw.icon), 1)
                                  ]),
                                  _: 2
                                }, 1024),
                                _createTextVNode(" " + _toDisplayString(item.raw.text), 1)
                              ]),
                              _: 2
                            }, 1032, ["color"])
                          ]),
                          _: 1
                        }, 8, ["modelValue", "items", "rules"])
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }),
                _createVNode(_component_v_row, null, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_col, { cols: "12" }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_text_field, {
                          modelValue: form.value.nc_title,
                          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((form.value.nc_title) = $event)),
                          label: "제목",
                          variant: "outlined",
                          placeholder: "공지사항 제목을 입력하세요",
                          rules: [v => !!v || '제목을 입력해주세요', v => v.length <= 200 || '제목은 200자 이내로 입력해주세요'],
                          counter: "200"
                        }, null, 8, ["modelValue", "rules"])
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }),
                _createVNode(_component_v_row, null, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_col, { cols: "12" }, {
                      default: _withCtx(() => [
                        _cache[8] || (_cache[8] = _createElementVNode("label", { class: "editor-label" }, "내용", -1)),
                        _createElementVNode("div", _hoisted_5, [
                          _createVNode(_unref(QuillEditor), {
                            ref_key: "quillRef",
                            ref: quillRef,
                            content: form.value.nc_content,
                            "onUpdate:content": _cache[2] || (_cache[2] = ($event: any) => ((form.value.nc_content) = $event)),
                            "content-type": "html",
                            options: editorOptions,
                            onReady: onEditorReady
                          }, null, 8, ["content"])
                        ])
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }),
                _createVNode(_component_v_row, null, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_col, { cols: "12" }, {
                      default: _withCtx(() => [
                        _cache[12] || (_cache[12] = _createElementVNode("label", { class: "editor-label" }, "첨부파일", -1)),
                        _createVNode(_component_v_file_input, {
                          modelValue: newFiles.value,
                          "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((newFiles).value = $event)),
                          label: "파일 선택",
                          variant: "outlined",
                          "prepend-icon": "mdi-attachment",
                          multiple: "",
                          "show-size": "",
                          rules: [v => !v || v.every((f) => f.size < 10 * 1024 * 1024) || '파일 크기는 10MB 이하여야 합니다'],
                          accept: ".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.hwp,.txt,.zip",
                          hint: "최대 5개, 각 파일 10MB 이하",
                          "persistent-hint": ""
                        }, null, 8, ["modelValue", "rules"]),
                        (existingFiles.value.length > 0)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                              _cache[11] || (_cache[11] = _createElementVNode("div", { class: "files-header" }, "기존 첨부파일", -1)),
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(existingFiles.value, (file) => {
                                return (_openBlock(), _createElementBlock("div", {
                                  key: file.nf_idx,
                                  class: "file-item"
                                }, [
                                  _createVNode(_component_v_icon, {
                                    size: "small",
                                    class: "mr-2"
                                  }, {
                                    default: _withCtx(() => [...(_cache[9] || (_cache[9] = [
                                      _createTextVNode("mdi-file", -1)
                                    ]))]),
                                    _: 1
                                  }),
                                  _createElementVNode("span", _hoisted_7, _toDisplayString(file.nf_original_name), 1),
                                  _createElementVNode("span", _hoisted_8, "(" + _toDisplayString(formatFileSize(file.nf_size)) + ")", 1),
                                  _createVNode(_component_v_btn, {
                                    icon: "",
                                    size: "small",
                                    variant: "text",
                                    color: "error",
                                    onClick: ($event: any) => (removeExistingFile(file))
                                  }, {
                                    default: _withCtx(() => [
                                      _createVNode(_component_v_icon, { size: "small" }, {
                                        default: _withCtx(() => [...(_cache[10] || (_cache[10] = [
                                          _createTextVNode("mdi-close", -1)
                                        ]))]),
                                        _: 1
                                      })
                                    ]),
                                    _: 1
                                  }, 8, ["onClick"])
                                ]))
                              }), 128))
                            ]))
                          : _createCommentVNode("", true)
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }, 8, ["modelValue"])
          ]),
          _: 1
        }),
        _createVNode(_component_v_divider),
        _createVNode(_component_v_card_actions, { class: "pa-4" }, {
          default: _withCtx(() => [
            _createVNode(_component_v_spacer),
            _createVNode(_component_v_btn, {
              variant: "outlined",
              onClick: goBack,
              disabled: saving.value
            }, {
              default: _withCtx(() => [...(_cache[13] || (_cache[13] = [
                _createTextVNode(" 취소 ", -1)
              ]))]),
              _: 1
            }, 8, ["disabled"]),
            _createVNode(_component_v_btn, {
              color: "primary",
              variant: "flat",
              onClick: saveNotice,
              loading: saving.value,
              disabled: !formValid.value
            }, {
              default: _withCtx(() => [
                _createVNode(_component_v_icon, { start: "" }, {
                  default: _withCtx(() => [...(_cache[14] || (_cache[14] = [
                    _createTextVNode("mdi-check", -1)
                  ]))]),
                  _: 1
                }),
                _createTextVNode(" " + _toDisplayString(isEdit.value ? '수정' : '등록'), 1)
              ]),
              _: 1
            }, 8, ["loading", "disabled"])
          ]),
          _: 1
        })
      ]),
      _: 1
    }),
    _createVNode(_component_v_snackbar, {
      modelValue: snackbar.value.show,
      "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((snackbar.value.show) = $event)),
      color: snackbar.value.color,
      timeout: 3000,
      location: "top"
    }, {
      default: _withCtx(() => [
        _createTextVNode(_toDisplayString(snackbar.value.text), 1)
      ]),
      _: 1
    }, 8, ["modelValue", "color"])
  ]))
}
}

})